import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Waves, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content: "Welcome aboard. I'm Nereus, Ocean Mind AI's deep-sea guide. Ask me anything about our AI automation services, process, or how we can help your business.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current) {
    let sid = localStorage.getItem("omai_session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("omai_session", sid);
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/history/${sessionRef.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((hist) => {
        if (hist.length) setMessages([GREETING, ...hist.map((m) => ({ role: m.role, content: m.content }))]);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.token };
                return copy;
              });
            } else if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: payload.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The currents are rough right now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-gradient-to-br from-[#00F2FE] to-[#10E59A] text-[#020813] flex items-center justify-center shadow-[0_0_35px_-5px_rgba(0,242,254,0.7)]"
        aria-label="Chat with Nereus"
      >
        {open ? <X className="w-6 h-6" /> : <Waves className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[90] w-[92vw] max-w-[390px] h-[540px] max-h-[70vh] rounded-3xl glass border border-[#00F2FE]/25 shadow-[0_0_60px_-15px_rgba(0,242,254,0.45)] flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-[#00F2FE]/15 flex items-center gap-3 bg-[#051221]/80">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00F2FE]/30 to-[#10E59A]/30 border border-[#00F2FE]/40 flex items-center justify-center">
                <Waves className="w-4 h-4 text-[#00F2FE]" />
              </span>
              <div className="flex-1">
                <p className="font-['Outfit'] font-medium text-sm text-[#E8F1F5]">Nereus</p>
                <p className="text-xs text-[#8AA1B1] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10E59A] animate-pulse-glow" />
                  Deep-sea guide · online
                </p>
              </div>
            </div>

            <div className="px-4 pt-3 bg-[#051221]/60">
              <a
                data-testid="chat-whatsapp-link"
                href="https://wa.me/447920279851?text=Hi%20Ocean%20Mind%20AI%2C%20I'd%20like%20to%20discuss%20a%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 text-[#25D366] text-xs font-medium py-2 hover:bg-[#25D366]/25 transition-colors duration-300"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Prefer a human? Chat on WhatsApp
              </a>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#00F2FE]/90 text-[#020813] rounded-br-md"
                        : "bg-[#051221] border border-[#00F2FE]/20 text-[#E8F1F5] rounded-bl-md"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#00F2FE]" />
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-[#00F2FE]/15 bg-[#051221]/80 flex items-center gap-2.5">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about our services..."
                className="flex-1 rounded-full bg-[#020813]/80 border border-[#00F2FE]/25 px-5 py-2.5 text-sm text-[#E8F1F5] placeholder:text-[#8AA1B1]/50 outline-none focus:border-[#00F2FE] transition-colors duration-300"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                className="w-10 h-10 shrink-0 rounded-full bg-[#00F2FE] text-[#020813] flex items-center justify-center hover:bg-[#10E59A] transition-colors duration-300 disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
